'use client';

import { useMemo } from 'react';
import { AssetFilterWithSearch } from '../../Inputs/AssetFilterWithSearch';
import Checkbox from '../../primitives/Checkbox/Checkbox';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import {
	usePerpMarketsOnlyTabs,
	useSpotMarketTabs,
} from 'src/constants/historyTables/filterConfigs';
import { AssetListAssetWithMarketIndex } from 'src/@types/assets';
import { UIMarket } from '@velocity-exchange/common';
import { SpotMarketConfig } from '@velocity-exchange/sdk';
import OrderHistoryMarketTypeFilter, {
	MarketTypeFilter,
} from 'src/components/HistoryTables/TradePageOrderHistoryTable/OrderHistoryMarketTypeFilter';
import OrderHistoryOrderStatusFilter, {
	OrderStatusFilter,
} from 'src/components/HistoryTables/TradePageOrderHistoryTable/OrderHistoryOrderStatusFilter';

type OrderHistoryFiltersProps = {
	overviewPage?: boolean;
};

export const OrderHistoryFilters = (_props: OrderHistoryFiltersProps) => {
	const filters = useHistoryTableFilterStore((s) => s.getFilterState('orders'));
	const setHistoryTableFilterState = useHistoryTableFilterStore((s) => s.set);
	const isMobile = useIsMobileScreenSize();

	const spotMarketTabs = useSpotMarketTabs();
	const perpMarketsOnlyTabs = usePerpMarketsOnlyTabs({
		withBaseAssetSymbol: true,
	});

	const assetFilterOptions = useMemo(() => {
		const marketTabs =
			filters.marketType === 'perp' ? perpMarketsOnlyTabs : spotMarketTabs;

		return marketTabs.map((market) => {
			if (market.value === 'all_markets') {
				return {
					mint: 'all_markets',
					ticker: 'all_markets',
					displayLabel: 'All Markets',
					marketIndex: -1,
				};
			}

			const uiMarket =
				filters.marketType === 'perp'
					? UIMarket.perpMarkets.find(
							(config) => config.symbol === market.value
						)
					: UIMarket.spotMarkets.find(
							(config) => config.symbol === market.value
						);

			const mint =
				filters.marketType === 'spot'
					? (uiMarket as SpotMarketConfig)?.mint?.toBase58() || market.value
					: undefined;

			return {
				mint,
				ticker: market.value,
				displayLabel: market.label,
				marketIndex: uiMarket?.marketIndex || 0,
			};
		}) satisfies AssetListAssetWithMarketIndex[];
	}, [filters.marketType, perpMarketsOnlyTabs, spotMarketTabs]);

	const handleMarketTypeFilterChange = (selectedFilter: MarketTypeFilter) => {
		setHistoryTableFilterState((s) => {
			s.orders.marketType = selectedFilter;
			// Reset market symbol when changing table type
			s.orders.marketSymbol = 'all_markets';
		});
	};

	const handleOrderStatusFilterChange = (selectedFilter: OrderStatusFilter) => {
		setHistoryTableFilterState((s) => {
			s.orders.showOnlyFilledOrders = selectedFilter === 'filled';
		});
	};

	const handleMarketFilterChange = (selectedMarket: string) => {
		setHistoryTableFilterState((s) => {
			s.orders.marketSymbol = selectedMarket;
		});
	};

	const handleFilledOrdersToggle = () => {
		setHistoryTableFilterState((s) => {
			s.orders.showOnlyFilledOrders = !s.orders.showOnlyFilledOrders;
		});
	};

	const currentFilledOrdersValue: OrderStatusFilter =
		filters.showOnlyFilledOrders ? 'filled' : 'all';

	if (isMobile) {
		return (
			<div className="flex w-full flex-col gap-y-md">
				<div className="flex w-full items-center justify-between gap-x-sm">
					<OrderHistoryMarketTypeFilter
						className="flex-1"
						filters={filters}
						handleTableFilterChange={handleMarketTypeFilterChange}
					/>
					<OrderHistoryOrderStatusFilter
						className="flex-1"
						currentFilledOrdersValue={currentFilledOrdersValue}
						handleTableFilterChange={handleOrderStatusFilterChange}
					/>
				</div>
				<AssetFilterWithSearch
					id="order-history-market-filter"
					currentSelection={filters.marketSymbol}
					options={assetFilterOptions}
					onChange={handleMarketFilterChange}
					label="Market"
					className="w-full"
				/>
			</div>
		);
	}

	return (
		<div className="flex w-full items-center justify-between">
			<div className="flex items-center gap-x-md">
				<OrderHistoryMarketTypeFilter
					className="min-w-[140px]"
					filters={filters}
					handleTableFilterChange={handleMarketTypeFilterChange}
				/>
				<AssetFilterWithSearch
					id="order-history-market-filter"
					currentSelection={filters.marketSymbol}
					options={assetFilterOptions}
					onChange={handleMarketFilterChange}
					label="Market"
					className="min-w-[200px]"
				/>
			</div>
			<Checkbox.Default
				id="show-only-filled-orders"
				label="Show only filled orders"
				checked={filters.showOnlyFilledOrders}
				onCheckedChange={handleFilledOrdersToggle}
			/>
		</div>
	);
};
